import React, { useEffect, useState, useMemo, useCallback, useRef, ReactNode } from 'react'
import { useNewTranslation } from 'hooks/index'
import { useDispatch, useSelector } from 'react-redux'
import { Paper, ThemeProvider, createTheme } from '@mui/material'
import {
    DataGridPro,
    GridRowsProp,
    GridApi,
    GridColumns,
    GridFilterModel,
    LicenseInfo,
    GridOverlayProps,
    useGridApiContext,
    GridSelectionModel,
    GridCallbackDetails,
    GridPinnedColumns,
    GridLinkOperator,
    GridRowHeightParams,
    GridRowHeightReturnValue,
    GridColDef,
    GridRowClassNameParams,
    GridPinnedRowsProp,
} from '@mui/x-data-grid-pro'

import { DecisionEditorStates } from 'components/Submissions/Review/common/DecisionEditor'
import {
    MAX_PAGE_SIZE,
    selectSelectedSubmissionView,
    selectActivePageNum,
    setActivePageNum,
    selectSelectedRegistrationView,
    setFilterModel,
    REGISTRATION_VIEW_ENUM,
    selectSearchTerm,
} from 'state/review'
import { selectViewedRows } from 'state/registrations'
import { STATE_KEYS } from 'state/requests'
import { createGetRequestPagination } from 'state'
import { SUBMISSION_VIEW_ENUM } from 'state/submissions'
import './index.css'
import BaseEmptyState from 'components/Shared/EmptyState'
import ReviewEmptyState from 'components/Submissions/Review/EmptyState'
import { theme } from 'theme'
import { getDataGridLocaleText } from './localeText'
import { PreferenceElState, SortItem } from './PartiTable'
import ColumnMenu from './ColumnMenu'
import Icon from 'components/Icon/Icon'
import styled from 'particles/themes/styled'
import ResizeIcon from './ResizeIcon'
import PreferencePanel from './ColumnMenu/PreferencePanel'
import { OptionsState } from 'particles/Dropdown'
import _ from 'lodash'
import { selectEditingCampaignUuid } from 'state/campaigns'
import Pagination from 'particles/Pagination'
import { useActiveFilter } from './hooks'

export enum TableViewType {
    PERFORMANCE = 'Performance',
    PROGRESS = 'Progress',
    BANNER_GROUP = 'BannerGroup',
    BANNER = 'Banner',
    ROLE_MANAGEMENT = 'RoleManagement',
    TEAM_LIST = 'TeamList',
    TEAM_MEMBER_LIST = 'TeamMemberList',
    BRAND_ACCESS = 'BrandAccess',
    SUBMISSIONS = 'Submissions',
    CAMPAIGN_OVERVIEW = 'CampaignOverview',
    CAMPAIGN_PERFORMANCE = 'CampaignPerformance',
    TASK_PERFORMANCE = 'TaskPerformance',
    INDIVIDUAL_PERFORMANCE = 'IndividualPerformance',
    TOP_PERFORMERS = 'TopPerformers',
    AMBASSADOR = 'Ambassador',
}

LicenseInfo.setLicenseKey(
    process.env.REACT_APP_MATERIAL_UI_LICENSE_KEY ? process.env.REACT_APP_MATERIAL_UI_LICENSE_KEY : '',
)

export const StyledDataGrid = styled(DataGridPro)`
    font-family: 'Clarika Geometric', Helvetica, Arial, 'Microsoft Yahei', 微软雅黑, STXihei, 华文细黑, sans-serif;
    line-height: 1.15 !important;

    & .MuiDataGrid-columnHeaders,
    .MuiDataGrid-pinnedColumnHeaders {
        color: white;
        background-color: ${theme.colors.brightGray};
        box-sizing: border-box;
    }
    &.MuiDataGrid-pinnedColumnHeaders {
        box-shadow: 0px 0px 5px orange;
    }
    & .MuiDataGrid-columnHeader {
        &.MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root {
            color: white;
            &.Mui-checked {
                color: white !important;
            }
        }

        & .MuiIconButton-root {
            color: white;
        }

        &:not(.MuiDataGrid-columnHeaderCheckbox) {
            & .MuiDataGrid-columnHeaderTitle,
            & .MuiDataGrid-columnHeaderTitleContainer {
                margin-right: 10px;
            }
            & .MuiDataGrid-menuIcon {
                margin-right: 0;
            }

            &:hover .MuiDataGrid-columnHeaderTitleContainer {
                margin-right: 0;
            }
        }
    }

    .MuiDataGrid-columnHeader:focus {
        outline: none; // Prevents the outline on appearing on focus
    }

    .MuiDataGrid-row {
        min-height: ${({ rowHeight }) =>
            rowHeight}px !important; // override the default min-height: auto for auto height row
        &.MuiDataGrid-row {
            &:hover,
            &.Mui-hovered {
                background-color: ${theme.newColors.primary50};
            }
            &.Mui-selected {
                background-color: ${theme.newColors.primary100};
                &:hover {
                    background-color: ${theme.newColors.primary100};
                }
            }
        }
    }

    .MuiButtonBase-root.MuiPaginationItem-root.Mui-selected {
        background-color: ${theme.newColors.primary300};
    }

    .custom-no-rows {
        position: absolute;
        width: 100%;
        transform: scale(0.85);
        zindex: 99;
        top: 0;
        bottom: 0;
        display: flex;
        align-content: center;
        flex-wrap: wrap;
    }
    .MuiCircularProgress-circle {
        color: ${theme.newColors.primary300};
    }
    .MuiDataGrid-columnSeparator--sideRight {
        right: -16px;
    }
`

const muiTheme = createTheme({
    palette: {
        primary: {
            main: theme.colors.astronaut,
        },
    },
})

const getCustomNoRowsComponent: (
    viewType?: TableViewType,
    customEmptyState?: ReactNode,
) => React.FC<GridOverlayProps> = (viewType, customEmptyState) =>
    function CustomNoRowsComponent() {
        const [newT] = useNewTranslation()
        const searchTerm = useSelector(selectSearchTerm)

        const getBaseEmptyTitle = () => {
            if (!_.isEmpty(searchTerm)) {
                return newT('emptyState.cantFindTitle')
            }

            switch (viewType) {
                case TableViewType.BANNER_GROUP:
                    return newT('emptyState.noBannerGroupTitle', 'No banner groups')
                case TableViewType.BANNER:
                    return newT('emptyState.noBannerTitle', 'No banners')
                case TableViewType.TEAM_MEMBER_LIST:
                    return newT('emptyState.noMembersAddedTitle')
                case TableViewType.BRAND_ACCESS:
                    return newT('emptyState.noBrandsAddedTitle')
                default:
                    return newT('emptyState.noSubmissionTitle')
            }
        }

        const getBaseEmptyBody = () => {
            if (!_.isEmpty(searchTerm)) {
                return newT('emptyState.noSearchResultSubmissionSubtitle')
            }

            switch (viewType) {
                case TableViewType.BANNER_GROUP:
                    return newT('emptyState.noBannerGroupSubtitle', 'There are no banner groups just yet!')
                case TableViewType.BANNER:
                    return newT('bannerGroup.banner.empty.body', 'There are no banners for this banner group just yet!')
                case TableViewType.TEAM_MEMBER_LIST:
                    return newT('emptyState.noMembersAddedSubtitle')
                case TableViewType.BRAND_ACCESS:
                    return newT('emptyState.noBrandsAddedSubtitle')
                default:
                    return newT('emptyState.noSubmissionSubtitle')
            }
        }

        return (
            <div className="custom-no-rows">
                {customEmptyState ? (
                    customEmptyState
                ) : viewType ? (
                    <BaseEmptyState
                        my={theme.spacing.l}
                        title={getBaseEmptyTitle()}
                        titleColor="primary"
                        body={getBaseEmptyBody()}
                        bodyColor="secondary"
                    />
                ) : (
                    <ReviewEmptyState />
                )}
            </div>
        )
    }

const CustomPaginationComponent: React.FC = () => {
    const apiRef = useGridApiContext()
    const { state } = apiRef.current
    const { rowCount: itemCount, pageSize } = state.pagination
    return (
        <Pagination
            totalItems={itemCount}
            itemPerPage={pageSize}
            onPageChange={page => {
                if (!page) return
                apiRef.current.setPage(page - 1)
            }}
        />
    )
}

interface MuiTableContainerProps {
    apiRef?: React.MutableRefObject<GridApi>
    title?: string
    rowHeight?: number
    columns: GridColumns
    rows: GridRowsProp
    onClickApprove?: any
    onClickRequestEdit?: any
    onClickReject?: any
    decisionEditorState?: {
        state: DecisionEditorStates | undefined
        data: any | undefined
    }
    isLoading: boolean
    viewType?: TableViewType
    customEmptyState?: ReactNode
    enableMultiSelect?: boolean
    onSelectionModelChange?: any
    tableHeight?: string
    disableColumnMenu?: boolean
    displayPagination?: boolean
    hideFooterRowCount?: boolean
    pinnedColumns?: GridPinnedColumns
    hideFooter?: boolean
    getRowHeight?: ((params: GridRowHeightParams) => GridRowHeightReturnValue) | undefined
    isNewStyle?: boolean
    className?: string
    preferenceEl: PreferenceElState
    onClosePreferencePanel: () => void
    onAddFilterField: (option: OptionsState) => void
    onAddSortField: (option: SortItem) => void
    headerHeight?: number
    getRowClassName?: (params: GridRowClassNameParams) => string
    pinnedRows?: GridPinnedRowsProp
}

const MuiTableContainer: React.FC<MuiTableContainerProps> = ({
    apiRef,
    title,
    rowHeight = 52,
    columns,
    rows,
    onClickApprove,
    onClickRequestEdit,
    onClickReject,
    decisionEditorState,
    isLoading,
    viewType,
    customEmptyState,
    enableMultiSelect = false,
    onSelectionModelChange,
    tableHeight = 'calc(100vh - 187px)',
    disableColumnMenu = false,
    displayPagination = true,
    hideFooterRowCount = false,
    pinnedColumns,
    hideFooter = false,
    getRowHeight,
    className,
    isNewStyle = false,
    preferenceEl,
    onClosePreferencePanel,
    onAddFilterField,
    onAddSortField,
    getRowClassName,
    headerHeight = 52,
    pinnedRows,
}) => {
    const [t, i18n] = useNewTranslation()
    const dispatch = useDispatch()
    const [selectedRowId, setSelectedRowId] = useState<number[]>([])
    const [visibleModel, setVisibleModel] = useState<{ [key: string]: boolean }>({})
    const selectedSubmissionView = useSelector(selectSelectedSubmissionView)
    const selectedRegistrationView = useSelector(selectSelectedRegistrationView)
    const filters = useActiveFilter(viewType)
    const viewedRows = useSelector(selectViewedRows)
    const campaignUuid = useSelector(selectEditingCampaignUuid)
    const preColumns = useRef<GridColumns | null>(null)

    const selectedStateKey = useMemo(() => {
        //TODO: Wait for design/pm to solve the reject and dropout flow fist to update the slectedStateKey about the dropout
        if (selectedRegistrationView !== null && selectedRegistrationView !== REGISTRATION_VIEW_ENUM.DROPOUTS) {
            return STATE_KEYS.RegistrationResultsList
        } else if (selectedSubmissionView === SUBMISSION_VIEW_ENUM.REJECTED) {
            return STATE_KEYS.RejectedList
        } else if (selectedSubmissionView === SUBMISSION_VIEW_ENUM.DROPOUTS) {
            return STATE_KEYS.DropoutsList
        } else {
            if (viewType === TableViewType.PERFORMANCE) {
                return STATE_KEYS.CampaignPerformance
            } else if (viewType === TableViewType.PROGRESS) {
                return STATE_KEYS.CampaignProgress
            } else if (viewType === TableViewType.BANNER_GROUP) {
                return STATE_KEYS.GetBannerGroups
            } else if (viewType === TableViewType.BANNER) {
                return STATE_KEYS.GetBanners
            } else if (viewType === TableViewType.TEAM_MEMBER_LIST) {
                return STATE_KEYS.TeamMembersList
            } else if (viewType === TableViewType.TEAM_LIST) {
                return STATE_KEYS.TeamsList
            } else if (viewType === TableViewType.INDIVIDUAL_PERFORMANCE) {
                return STATE_KEYS.GetIndividualPerformance
            } else if (viewType === TableViewType.AMBASSADOR) {
                return STATE_KEYS.AmbassadorList
            } else {
                return STATE_KEYS.SubmissionsList
            }
        }
    }, [selectedSubmissionView, viewType, selectedRegistrationView])
    const activePageNum = useSelector(selectActivePageNum)
    const pagination = useSelector(createGetRequestPagination(selectedStateKey))
    const itemCount = pagination.count ? pagination.count : 0

    useEffect(() => {
        if (selectedRowId.length > 0 && decisionEditorState && decisionEditorState.state == undefined) {
            setSelectedRowId([])
        }
    }, [decisionEditorState, selectedRowId])

    const handlePageChange = (page: number) => {
        dispatch(setActivePageNum(page))
    }
    const handleFilterModelChange = useCallback(
        (filterModel: GridFilterModel) => {
            if (selectedRegistrationView && campaignUuid) {
                if (filterModel.items && filterModel.items.length > 0) {
                    dispatch(setFilterModel({ viewType: selectedRegistrationView, campaignUuid, options: filterModel }))
                } else if (!filterModel || !filterModel.items || filterModel.items.length === 0) {
                    dispatch(setFilterModel({ viewType: selectedRegistrationView, campaignUuid, options: null }))
                }
            }
        },
        [selectedRegistrationView, campaignUuid],
    )

    const handleSelectionModelChange = useCallback(
        (selectionModel: GridSelectionModel, details: GridCallbackDetails) => {
            setSelectedRowId(selectionModel as number[])
            if (onSelectionModelChange) {
                onSelectionModelChange(selectionModel, details)
            }
        },
        [onSelectionModelChange],
    )
    const handleVisibleModel = useCallback((newModel: Record<string, boolean>) => {
        setVisibleModel(newModel)
    }, [])

    const localeText = useMemo(() => getDataGridLocaleText(i18n.language, t), [i18n.language, t])
    useEffect(() => {
        if (!preColumns.current || !_.isEqual(preColumns.current, columns)) {
            preColumns.current = columns
        }
    }, [columns])

    //To compare if the columns are actually changed or just cell value changed to keep the hided columns setting
    const isColumnsChanged = useMemo(() => {
        if (preColumns.current !== null) {
            const preColumnFields = preColumns.current.map((element: GridColDef) => element.field)
            return columns.some((column: GridColDef) => !preColumnFields.includes(column.field))
        }
        return false
    }, [columns])

    useEffect(() => {
        if (Array.isArray(preColumns.current)) {
            const visibleColumns: Record<string, boolean> = preColumns.current.reduce(
                (result: Record<string, boolean>, item: GridColDef) => {
                    result[item.field] = true
                    return result
                },
                {},
            )
            setVisibleModel(visibleColumns)
        }
    }, [isColumnsChanged])

    const isFilterable = (field: string) => {
        return Boolean(Object.keys(filters).find(filterKey => filterKey.includes(field)))
    }

    return (
        <Paper
            style={{ height: tableHeight, width: '100%' }}
            className={`${className} ${isNewStyle && 'new-style-MUI'}`}
        >
            <ThemeProvider theme={muiTheme}>
                <StyledDataGrid
                    // note: if the key changes, the component will be re-mounted;
                    // it will begin at its initial state (including resetting the filters)
                    key={title}
                    apiRef={apiRef}
                    rows={rows}
                    columns={columns}
                    columnBuffer={columns.length}
                    onColumnVisibilityModelChange={(newModel: Record<string, boolean>) => {
                        const visibleModel = { ...newModel }
                        if (!isColumnsChanged) {
                            handleVisibleModel(visibleModel)
                        }
                    }}
                    // auto height / dynamic height
                    autoHeight={tableHeight === 'auto'}
                    columnVisibilityModel={visibleModel}
                    componentsProps={{
                        toolbar: {
                            title,
                            onClickApprove,
                            onClickRequestEdit,
                            onClickReject,
                            setSelectedRowId,
                        },
                        // since current backend not support OR filter, so we disable the OR operators by default.
                        filterPanel: {
                            linkOperators: [GridLinkOperator.And],
                        },
                        columnMenu: {
                            apiRef: apiRef,
                            visibleModel: visibleModel,
                            setVisibleModel: handleVisibleModel,
                            onAddFilterField: onAddFilterField,
                            onAddSortField: onAddSortField,
                            isFilterable: isFilterable,
                        },
                        preferencesPanel: {
                            preferenceEl: preferenceEl,
                            onClosePreferencePanel: onClosePreferencePanel,
                            visibleModel: visibleModel,
                            setVisibleModel: handleVisibleModel,
                        },
                    }}
                    components={{
                        ColumnMenu: ColumnMenu,
                        NoRowsOverlay: getCustomNoRowsComponent(viewType, customEmptyState),
                        NoResultsOverlay: getCustomNoRowsComponent(viewType, customEmptyState),
                        Pagination: CustomPaginationComponent,
                        ColumnMenuIcon: Icon.ShowToolTipIcon,
                        GroupingCriteriaExpandIcon: Icon.AgeIcon,
                        ColumnResizeIcon: ResizeIcon,
                        PreferencesPanel: PreferencePanel,
                    }}
                    rowHeight={rowHeight}
                    selectionModel={selectedRowId}
                    disableSelectionOnClick
                    page={activePageNum}
                    pageSize={MAX_PAGE_SIZE}
                    rowCount={itemCount}
                    pagination={displayPagination}
                    hideFooterRowCount={hideFooterRowCount}
                    paginationMode={'server'}
                    onPageChange={handlePageChange}
                    filterMode={'server'}
                    onFilterModelChange={handleFilterModelChange}
                    loading={isLoading}
                    localeText={localeText}
                    checkboxSelection={enableMultiSelect}
                    onSelectionModelChange={handleSelectionModelChange}
                    disableColumnMenu={disableColumnMenu}
                    pinnedColumns={pinnedColumns}
                    hideFooter={hideFooter}
                    getRowHeight={getRowHeight}
                    getCellClassName={params => {
                        if (
                            selectedRegistrationView === REGISTRATION_VIEW_ENUM.PENDING &&
                            viewedRows.includes(params.row?.uuid) &&
                            params.field !== 'partiposterTags'
                        ) {
                            return 'viewed-cell'
                        }
                        return ''
                    }}
                    headerHeight={headerHeight}
                    getRowClassName={getRowClassName}
                    pinnedRows={pinnedRows}
                    experimentalFeatures={{ rowPinning: rows.length > 0 && pinnedRows !== undefined }}
                />
            </ThemeProvider>
        </Paper>
    )
}

export default styled(MuiTableContainer)`
    .MuiDataGrid-root {
        font-family: 'Clarika Geometric', Helvetica, Arial, 'Microsoft Yahei', 微软雅黑, STXihei, 华文细黑, sans-serif !important;
    }
    .viewed-cell {
        .cellValue {
            div {
                color: ${theme.newColors.neutral500};
            }
            color: ${theme.newColors.neutral500};
        }
    }
    .MuiDataGrid-cell:focus-within {
        outline: 1px solid ${theme.newColors.primary500};
    }
    .MuiDataGrid-cell {
        padding: 8px 12px !important;
    }
    &.new-style-MUI {
        .MuiDataGrid-columnHeaderTitle {
            color: ${theme.newColors.neutral50};
            font-size: ${theme.newFontSizes.s};
            font-weight: ${theme.fontWeight.medium};
            line-height: ${theme.lineHeight.m};
            height: 100%;
        }
        .MuiDataGrid-cell {
            border-left: 1px solid ${theme.newColors.neutral100};
        }
        .MuiDataGrid-cellContent {
            color: ${theme.newColors.neutral700};
            font-size: ${theme.newFontSizes.m};
            font-weight: ${theme.fontWeight.medium};
            line-height: ${theme.lineHeight.l};
        }
    }
`
